
import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { Plan } from "./usePricingPlans";
import { hasActivePaidSubscription } from "@/utils/subscriptionUtils";
import { useNavigate } from "react-router-dom";
import { generatePathWithParams } from "@/utils/routerUtils";

export const usePlanSelection = (returnUrl?: string) => {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSelectPlan = useCallback(async (plan: Plan) => {
    // Don't allow selection of disabled plans
    if (plan.is_disabled) {
      toast({
        title: "Plan Unavailable",
        description: plan.disabled_text || "This plan is not available yet",
        variant: "default",
      });
      return;
    }

    setSelectedPlan(plan);

    if (plan.price === 0) {
      // Free plan functionality
      toast({
        title: "Free Plan Selected",
        description: "You're now on the Free plan",
        variant: "default",
      });
      
      // Redirect to the appropriate page
      if (returnUrl) {
        // If a return URL is provided, use it
        const separator = returnUrl.includes('?') ? '&' : '?';
        window.location.href = `${returnUrl}${separator}t=${new Date().getTime()}`;
      } else {
        // Navigate to the service-share page
        navigate("/service-share");
      }
    } else {
      // Check if user already has a paid subscription
      try {
        const hasPaidSubscription = await hasActivePaidSubscription();
        
        if (hasPaidSubscription) {
          toast({
            title: "Change Plan",
            description: "You'll be upgrading or downgrading your current plan",
            variant: "default",
          });
        }
        
        // Show payment dialog for paid plans
        setPaymentDialogOpen(true);
      } catch (error) {
        console.error("Error checking subscription status:", error);
        setPaymentDialogOpen(true); // Still allow payment attempt if check fails
      }
    }
  }, [toast, returnUrl, navigate]);

  return {
    selectedPlan,
    paymentDialogOpen,
    setPaymentDialogOpen,
    handleSelectPlan
  };
};
